import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { ScrollText, Search } from 'lucide-react';

interface Log {
  id: string;
  hotel_name: string;
  saas_company: string;
  endpoint: string;
  method: string;
  response_status: number;
  created_at: string;
}

export function InternalLogs() {
  const [logs, setLogs] = useState<Log[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const { data, error: queryError } = await supabase
        .from('internal_logs_secure')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (queryError) throw queryError;

      setLogs(data || []);
    } catch (err) {
      setError('Failed to load logs');
      console.error('Error loading logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => 
    log.hotel_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.saas_company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.endpoint.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="text-gray-500">Loading logs...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center space-x-2">
          <ScrollText className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">System Logs</h2>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50">
            {error}
          </div>
        )}

        <div className="mb-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by hotel, SaaS company, or endpoint"
                  className="form-input pl-10"
                />
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Timestamp</th>
                <th className="table-header">Hotel</th>
                <th className="table-header">SaaS Company</th>
                <th className="table-header">Method</th>
                <th className="table-header">Endpoint</th>
                <th className="table-header">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="table-row">
                  <td className="table-cell">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="table-cell">
                    {log.hotel_name}
                  </td>
                  <td className="table-cell">
                    {log.saas_company}
                  </td>
                  <td className="table-cell">
                    <span className={`status-badge ${
                      log.method === 'GET'
                        ? 'bg-blue-100'
                        : log.method === 'POST'
                        ? 'bg-green-100'
                        : log.method === 'PUT'
                        ? 'bg-yellow-100'
                        : 'bg-red-100'
                    }`}>
                      {log.method}
                    </span>
                  </td>
                  <td className="table-cell">
                    {log.endpoint}
                  </td>
                  <td className="table-cell">
                    <span className={`status-badge ${
                      log.response_status >= 200 && log.response_status < 300
                        ? 'status-active'
                        : 'status-error'
                    }`}>
                      {log.response_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredLogs.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchTerm
                  ? 'No logs found matching your search criteria.'
                  : 'No logs found.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}